import { nodesMapper } from "@features/builder/helpers/nodesMapper"
import { useGoToNode } from "@features/builder/hooks/useGoToNode"
import type { AppNode } from "@features/builder/graph/types"
import type { ValidationType } from "@features/builder/graph/nodes/validation/types"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material"
import { useNodes } from "@xyflow/react"
import { useMemo, useState } from "react"

export const NodesErrors = () => {
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set())
  const { getValidationErrors, nodesValidation } = useNodesValidation()
  const nodes = useNodes<AppNode>()
  const mapper = useMemo(() => nodesMapper(nodes), [nodes])
  const goToNode = useGoToNode()

  const nodeRows = useMemo(
    () =>
      Object.entries(nodesValidation)
        .map(([nodeId, validations]) => {
          const entries = (Object.keys(validations) as ValidationType[]).flatMap((validationKey) =>
            getValidationErrors(validationKey, [nodeId])
              .map((error) => error.msg.trim())
              .filter((error) => error.length > 0)
              .map((error) => ({ validationKey, error })),
          )

          return {
            nodeId,
            nodeLabel: mapper.idToLabelMap.get(nodeId) ?? nodeId,
            entries,
          }
        })
        .filter((node) => node.entries.length > 0),
    [getValidationErrors, mapper, nodesValidation],
  )

  const areAllNodesExpanded = nodeRows.length > 0 && nodeRows.length === expandedNodeIds.size

  const handleNodeAccordionChange =
    (nodeId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedNodeIds((nodeIds) => {
        const nextNodeIds = new Set(nodeIds)

        if (isExpanded) nextNodeIds.add(nodeId)
        else nextNodeIds.delete(nodeId)

        return nextNodeIds
      })
    }

  const handleToggleExpandAllNodes = () => {
    if (areAllNodesExpanded) {
      setExpandedNodeIds(new Set())
      return
    }

    setExpandedNodeIds(new Set(nodeRows.map(({ nodeId }) => nodeId)))
  }

  return (
    <>
      <Stack>
        <Button size="small" disabled={nodeRows.length === 0} onClick={handleToggleExpandAllNodes}>
          {areAllNodesExpanded ? "Collapse all" : "Expand all"}
        </Button>
      </Stack>
      {nodeRows.map(({ nodeId, nodeLabel, entries }) => (
        <Accordion
          key={nodeId}
          disableGutters
          expanded={expandedNodeIds.has(nodeId)}
          onChange={handleNodeAccordionChange(nodeId)}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              gap={0.5}
              direction="row"
              onClick={(event) => {
                event.stopPropagation()
                goToNode(nodeId)
              }}
            >
              <Typography component="span" sx={{ color: "error.main" }}>
                [{entries.length}]
              </Typography>
              <Typography component="span">{nodeLabel}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <List dense disablePadding>
              {entries.map(({ validationKey, error }, index) => (
                <ListItemButton
                  key={`${nodeId}-${validationKey}-${index}`}
                  dense
                  onClick={() => goToNode(nodeId)}
                >
                  <ListItemText
                    primary={`- ${error}`}
                    primaryTypographyProps={{ color: "error.main" }}
                    sx={{ my: 0 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  )
}
